import api from './api';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/** 1 step của saga đồng bộ chứng từ, hiển thị trong badge chi tiết hoá đơn. */
export interface SyncBadgeStep {
  key: string;
  state: 'PENDING' | 'DONE' | 'ERROR';
  last_error?: string;
  error_class?: 'TRANSIENT' | 'HARD';
}

export type SyncStateValue = 'WAITING' | 'PROCESSING' | 'DONE' | 'ERROR';

/**
 * 1 chứng từ do hệ kế toán đích tạo ra từ hoá đơn này.
 *
 * `target` nằm trong từng phần tử (hiện chỉ `amis`) nên khi nối thêm đích khác, cột
 * "Chứng từ tham chiếu" không phải đổi gì — chỉ hiện thêm dòng với `target` mới.
 */
export interface ExternalVoucherRef {
  target: string;
  /** Loại chứng từ bên đích, vd `sa_voucher` | `sa_invoice` | `in_outward` | `ca_receipt`. */
  type: string;
  refid: string;
  no?: string | null;
  created_at?: string;
}

/** TCR-2461 — khả năng sửa loại chiết khấu trên AMIS; `reason` có khi `eligible=false`. */
export interface AmisDiscountFixStatus {
  eligible: boolean;
  reason?: string;
  sync: SyncBadge | null;
}

/**
 * Badge trạng thái đồng bộ của 1 hoá đơn (field optional `sync` trên detail, join từ
 * `accounting_outbox` phía BE — vắng mặt khi hoá đơn chưa từng đồng bộ).
 */
export interface SyncBadge {
  state: SyncStateValue;
  steps: SyncBadgeStep[];
  /** Định danh KỸ THUẬT bên hệ đích (GUID). Không phải thứ để hiện cho kế toán đọc. */
  ref_code?: string;
  /**
   * SỐ CHỨNG TỪ đọc được bên hệ đích — `CK00004`, `BH964698`... Đây mới là "chứng từ tham chiếu"
   * kế toán cần thấy; `ref_code` là GUID. Vắng mặt khi chứng từ chưa được tạo bên đích.
   */
  ref_no?: string;
  attempts: number;
  next_run_at?: string;
  /**
   * Lý do ô "Nhân viên bán hàng" trên AMIS bị để trống (TCR-2492) — chỉ có ở badge chi tiết.
   * `no_employee` là chứng từ không gắn nhân viên nên không cần cảnh báo.
   */
  employee_skip_reason?: 'no_employee' | 'unmapped' | 'changed' | 'deleted';
}

/**
 * Chi tiết chứng từ bán hàng phái sinh (`sales_return` / `sales_discount`). Field thân
 * chứng từ khác nhau theo từng `kind` nên để index signature; riêng `sync` khai tường minh
 * để màn chi tiết đọc badge đồng bộ AMIS mà không phải ép kiểu. `sync` vắng mặt khi chứng
 * từ chưa từng vào hàng đợi đồng bộ.
 */
export interface SalesDocumentDetail {
  [key: string]: unknown;
  sync?: SyncBadge;
}

export interface SalesInvoice {
  id: string;
  _id?: string;
  invoice_number: string;
  invoice_no?: string;
  invoiceNumber?: string;
  invoice_date: string;
  invoiceDate?: string;
  date?: string;
  customer_id: string;
  employee_id?: string;
  customer_code?: string;
  customer_name?: string;
  customer_address?: string;
  customer_phone?: string;
  customer_tax_code?: string;
  store_id?: string;
  store_name?: string;
  total_amount: number;
  totalAmount?: number;
  vat_amount: number;
  vatAmount?: number;
  discount_amount: number;
  discountAmount?: number;
  net_amount: number;
  netAmount?: number;
  final_amount: number;
  vat_rate?: number;
  status: 'draft' | 'posted' | 'cancelled' | 'replaced';
  payment_status?: 'unpaid' | 'partial' | 'paid';
  offset_amount?: number;
  remaining_amount?: number;
  is_paid?: boolean;
  /**
   * TCR-1558 — hình thức hạch toán của header, tách khỏi is_paid/payment_method.
   * Optional cho tới khi migration backfill xong dữ liệu cũ; chứng từ cũ đọc field
   * này là `undefined` và phải suy luận qua `resolveInvoicePostingMode`.
   */
  settlement_type?: 'receivable' | 'direct_cash' | 'direct_bank';
  payment_method?: 'cash' | 'bank_transfer' | 'split';
  fund_account_id?: string;
  bank_ref?: string;
  payment_allocations?: Array<{
    method?: 'cash' | 'bank_transfer';
    amount?: number;
    fund_account_id?: string;
    bank_ref?: string;
  }>;
  /** Phiếu thu đã tất toán hóa đơn — hóa đơn cũ chỉ có 1 phiếu, field số nhiều dành cho tất toán nhiều lần/nhiều phiếu. */
  receipt_voucher_id?: string;
  receipt_voucher_ids?: string[];
  receipt_voucher_number?: string;
  invoice_tax_type?: 'vat' | 'hkd_direct' | 'none';
  invoice_category?: 'goods' | 'service';
  /** Tab đang xem trên danh sách chứng từ; dùng để export đúng tập bản ghi đang hiển thị. */
  document_case?: 'sale' | 'service';
  description?: string;
  created_by?: string;
  createdBy?: string;
  is_e_invoice?: boolean;
  include_e_invoice?: boolean;
  e_invoice_status?: 'draft' | 'pending' | 'processing' | 'success' | 'issued' | 'cancelled' | 'failed' | 'adjusting';
  cqt_status?: 'pending' | 'sent' | 'failed' | 'not_required';
  einvoice_id?: string;
  items?: SalesInvoiceItem[];
  created_at?: string;
  createdAt?: string;
  /** Badge list (chỉ state), field optional, vắng mặt khi chưa từng đồng bộ. */
  sync_state?: SyncStateValue;
  /** Badge chi tiết (detail only), join từ `accounting_outbox`. */
  sync?: SyncBadge;
  /** Đơn hàng sinh ra hoá đơn — null với hoá đơn lập tay. */
  order_id?: string | null;
  order_number?: string | null;
  /** Chứng từ đã tạo bên hệ kế toán đích, ghi ngược khi outbox sync xong. */
  external_vouchers?: ExternalVoucherRef[];
  external_synced_at?: string | null;
}

interface SalesInvoiceItem {
  line_id?: string;
  item_type?: 'goods' | 'service';
  /** Dòng ghi chú không tham gia tính tiền; một số API/record cũ dùng note_text. */
  is_note?: boolean;
  note_text?: string;
  isService?: boolean;
  product_id?: string;
  service_id?: string;
  service_code?: string;
  service_name?: string;
  variant_id?: string;
  variant_code?: string;
  variant_name?: string;
  warehouse_id?: string;
  warehouse_code?: string;
  warehouse_name?: string;
  product_code?: string;
  product_name?: string;
  uom?: string;
    quantity: number;
    unit_price: number;
    cost_price?: number;
    cogs_unit_price?: number;
  vat_rate?: number;
    discount_amount: number;
  tax_amount: number;
  total_amount: number;
  vat_rate_id?: string;
  vat_rate_code?: string;
  vat_source?: string;
  tax_industry_group_id?: string;
  hkd_industry_tax_rate_id?: string;
  vat_conversion_rule_id?: string;
  revenue_account_code?: string;
  ar_account_code?: string;
  inventory_account_code?: string;
  cogs_account_code?: string;
  serials?: string[];
  tracking_mode?: string;
  inventory_item_group_id?: string;
  inventory_item_group_nature?: string;
}

interface SalesInvoiceListParams {
  page?: number;
  page_size?: number;
  search?: string;
  status?: string;
  from?: string;
  to?: string;
  customer_id?: string;
  store_id?: string;
  invoice_category?: 'goods' | 'service';
  invoice_issue_status?: 'issued' | 'unissued';
  sync_status?: 'DONE' | 'ERROR' | 'NOT_SYNCED';
  /** Lọc theo loại hóa đơn — `original` loại bỏ hóa đơn thay thế/điều chỉnh (TCR-1384). */
  invoice_type?: string;
}

interface SalesInvoiceListResponse {
  items: SalesInvoice[];
  meta: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

interface CreateSalesInvoiceDto {
  invoice_date: string;
  invoice_category?: 'goods' | 'service';
  store_id?: string;
  customer_id: string;
  items: {
    line_id?: string;
    item_type?: 'goods' | 'service';
    product_id?: string;
    service_id?: string;
    service_code?: string;
    variant_id?: string;
    variant_code?: string;
    variant_name?: string;
    warehouse_id?: string;
    product_code?: string;
    product_name?: string;
    description?: string;
    uom?: string;
    quantity: number;
    unit_price: number;
    cost_price?: number;
    cogs_unit_price?: number;
    vat_rate: number;
    vat_rate_id?: string;
    vat_rate_code?: string;
    vat_source?: string;
    tax_industry_group_id?: string;
    hkd_industry_tax_rate_id?: string;
    vat_conversion_rule_id?: string;
    revenue_account_code?: string;
    ar_account_code?: string;
    inventory_account_code?: string;
    cogs_account_code?: string;
    deferred_start_date?: string;
    deferred_periods?: number;
    deferred_recognition_account_code?: string;
    serials?: string[];
    tracking_mode?: string;
    inventory_item_group_id?: string;
    inventory_item_group_nature?: string;
    line_discount_type?: 'amount' | 'percent';
    line_discount_value?: number;
    vat_reduction_applied?: boolean;
  }[];
  invoice_discount_type?: 'amount' | 'percent';
  invoice_discount_value?: number;
  payment_terms?: string;
  payment_terms_days?: number;
  due_date?: string;
  warehouse_id?: string;
  is_paid?: boolean;
  payment_method?: 'cash' | 'bank_transfer' | 'split';
  fund_account_id?: string;
  payment_allocations?: Array<{
    method: 'cash' | 'bank_transfer';
    amount: number;
    fund_account_id?: string;
    bank_ref?: string;
  }>;
  bank_ref?: string;
  auto_issue_delivery?: boolean;
  include_e_invoice?: boolean;
  invoice_tax_type?: 'vat' | 'hkd_direct' | 'none';
}

interface PreviewAccountsDto {
  items: {
    line_id?: string;
    product_id?: string;
    service_id?: string;
    service_code?: string;
    revenue_account_code?: string;
    default_revenue_account_code?: string;
    inventory_account_code?: string;
    cogs_account_code?: string;
    inventory_item_group_id?: string;
    item_type?: 'goods' | 'service';
  }[];
}

export interface PreviewAccountResult {
  line_id?: string;
  product_id?: string;
  service_id?: string;
  service_code?: string;
  item_type?: 'goods' | 'service';
  revenue_account_code: string;
  revenue_account_id?: string;
  revenue_account_source: string;
  deferred_recognition_account_code?: string;
  inventory_account_code: string;
  inventory_account_id?: string;
  cogs_account_code: string;
  cogs_account_id?: string;
  cogs_account_source: string;
  inventory_item_group_id?: string;
  inventory_item_group_code?: string;
  inventory_item_group_nature?: string;
}

export interface UnpostImpactResponse {
  allowed: boolean;
  blockers: string[];
  linked_documents: Array<{
    kind: string;
    document_type: string;
    id: string;
    number: string;
    action: string;
    href?: string;
    status?: string;
  }>;
  confirmation_token: string;
  document_version: number;
  edit_path: string;
}

export interface UnpostDocumentDto {
  reason: string;
  document_version: number;
  confirmation_token: string;
}

export interface UnpostOperationResult {
  operation_id: string;
  status: string;
  document_status: string;
  edit_path?: string;
}

export type UpdateSalesInvoiceDto = Partial<CreateSalesInvoiceDto> & { id: string };

/**
 * TCR-1050 — một hoá đơn của đơn hàng kèm số còn nợ, nguồn cho ô "Đơn hàng" của form
 * phiếu thu. BE (`order-receivable-invoice.service.ts`) đã lọc bỏ hoá đơn
 * `draft`/`cancelled`/`replaced` và sắp theo `invoice_date` tăng dần (nợ cũ trước).
 */
export interface OrderReceivableInvoice {
  invoice_id: string;
  invoice_number: string;
  invoice_date: string | null;
  receivable_amount: number;
  paid_amount: number;
  outstanding_amount: number;
  ar_account_code: string;
}

/**
 * Service for Sales Invoice operations
 * Handles: Create, read, update, delete sales invoices
 */
export const salesInvoiceService = {
  /**
   * Get list of sales invoices with filters
   * GET /accounting/sales-invoices
   */
  getList(params: SalesInvoiceListParams): Promise<ApiResponse<SalesInvoiceListResponse>> {
    return api.get('/accounting/sales-invoices', { params }).then((r) => r.data);
  },

  /**
   * Get sales invoice detail
   * GET /accounting/sales-invoices/:id
   */
  getDetail(id: string, options?: { scenarioIntegrationArtifact?: boolean }): Promise<ApiResponse<SalesInvoice>> {
    const suffix = options?.scenarioIntegrationArtifact ? '/scenario-integration-artifact' : '';
    return api.get(`/accounting/sales-invoices/${id}${suffix}`).then((r) => r.data);
  },

  /**
   * TCR-1050 — hoá đơn còn phải thu của một đơn hàng
   * GET /accounting/sales-invoices/by-order/:order_id
   *
   * Đơn chưa xuất hoá đơn (thu cọc / trả trước) trả mảng rỗng — không phải lỗi.
   */
  listByOrder(orderId: string): Promise<ApiResponse<OrderReceivableInvoice[]>> {
    return api.get(`/accounting/sales-invoices/by-order/${orderId}`).then((r) => r.data);
  },

  /**
   * Create new sales invoice
   * POST /accounting/sales-invoices
   */
  create(data: CreateSalesInvoiceDto): Promise<ApiResponse<SalesInvoice>> {
    return api.post('/accounting/sales-invoices/manual', data).then((r) => r.data);
  },

  /**
   * Preview revenue, inventory and COGS accounts for invoice lines
   * POST /accounting/sales-invoices/preview-accounts
   */
  previewAccounts(data: PreviewAccountsDto): Promise<ApiResponse<PreviewAccountResult[]>> {
    return api.post('/accounting/sales-invoices/preview-accounts', data).then((r) => r.data);
  },

  /**
   * Update sales invoice
   * PATCH /accounting/sales-invoices/:id
   */
  update(id: string, data: Partial<CreateSalesInvoiceDto>): Promise<ApiResponse<SalesInvoice>> {
    return api.put(`/accounting/sales-invoices/${id}`, data).then((r) => r.data);
  },

  /**
   * Delete sales invoice (only draft)
   * DELETE /accounting/sales-invoices/:id
   */
  delete(id: string): Promise<ApiResponse<void>> {
    return api.delete(`/accounting/sales-invoices/${id}`).then((r) => r.data);
  },

  /**
   * Post sales invoice (ghi sổ)
   * POST /accounting/sales-invoices/:id/post
   */
  post(id: string): Promise<ApiResponse<SalesInvoice>> {
    return api.post(`/accounting/sales-invoices/${id}/post`).then((r) => r.data);
  },

  /**
   * Cancel sales invoice
   * POST /accounting/sales-invoices/:id/cancel
   */
  cancel(id: string, reason?: string): Promise<ApiResponse<SalesInvoice>> {
    return api.post(`/accounting/sales-invoices/${id}/cancel`, { cancel_reason: reason }).then((r) => r.data);
  },

  /**
   * B9 — "Thử lại" đồng bộ AMIS (task ERROR do lỗi hạ tầng, giữ snapshot cũ).
   * POST /accounting/sales-invoices/:id/accounting-sync/retry
   */
  retrySync(id: string): Promise<ApiResponse<SyncBadge>> {
    return api.post(`/accounting/sales-invoices/${id}/accounting-sync/retry`).then((r) => r.data);
  },

  /**
   * B9 — "Cập nhật & đồng bộ lại" AMIS (rebuild snapshot từ hoá đơn hiện tại).
   * POST /accounting/sales-invoices/:id/accounting-sync/resync
   */
  resync(id: string): Promise<ApiResponse<SyncBadge>> {
    return api.post(`/accounting/sales-invoices/${id}/accounting-sync/resync`).then((r) => r.data);
  },

  /**
   * TCR-2461 — chứng từ AMIS có sửa được loại chiết khấu không + trạng thái task sửa.
   * GET /accounting/sales-invoices/:id/accounting-sync/amis-discount
   */
  getAmisDiscountStatus(id: string): Promise<ApiResponse<AmisDiscountFixStatus>> {
    return api
      .get(`/accounting/sales-invoices/${id}/accounting-sync/amis-discount`)
      .then((r) => r.data);
  },

  /**
   * TCR-2461 — xếp task sửa loại chiết khấu trên AMIS (400 kèm lý do khi không đủ điều kiện).
   * POST /accounting/sales-invoices/:id/accounting-sync/amis-discount
   */
  fixAmisDiscount(id: string): Promise<ApiResponse<AmisDiscountFixStatus>> {
    return api
      .post(`/accounting/sales-invoices/${id}/accounting-sync/amis-discount`)
      .then((r) => r.data);
  },

  /**
   * "Thử lại" đồng bộ AMIS cho chứng từ bán hàng phái sinh (`kind` = `sales_discount`...) —
   * giữ nguyên snapshot cũ, chỉ chạy lại task đang lỗi.
   * POST /accounting/sales-documents/:kind/:id/accounting-sync/retry
   */
  retrySalesDocumentSync(kind: string, id: string): Promise<ApiResponse<SyncBadge>> {
    return api
      .post(`/accounting/sales-documents/${kind}/${id}/accounting-sync/retry`)
      .then((r) => r.data);
  },

  /**
   * "Cập nhật & đồng bộ lại" AMIS cho chứng từ bán hàng phái sinh — dựng lại snapshot từ
   * chứng từ hiện tại rồi đồng bộ.
   * POST /accounting/sales-documents/:kind/:id/accounting-sync/resync
   */
  resyncSalesDocument(kind: string, id: string): Promise<ApiResponse<SyncBadge>> {
    return api
      .post(`/accounting/sales-documents/${kind}/${id}/accounting-sync/resync`)
      .then((r) => r.data);
  },

  /**
   * Export sales invoices to Excel/CSV
   * POST /accounting/sales-invoices/export
   */
  export(params: Omit<SalesInvoiceListParams, 'page' | 'limit'>): Promise<Blob> {
    return api
      .post('/accounting/sales-invoices/export', params, { responseType: 'blob' })
      .then((r) => r.data);
  },

  /**
   * Import sales invoices from Excel/CSV
   * POST /accounting/sales-invoices/import
   */
  import(
    file: File,
    options?: {
      mapping?: Record<string, string>;
      sheetIndex?: number;
      headerRow?: number;
    },
  ): Promise<ApiResponse<{ imported: number; errors: string[] }>> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.mapping) formData.append('mapping', JSON.stringify(options.mapping));
    if (options?.sheetIndex !== undefined) formData.append('sheet_index', String(options.sheetIndex));
    if (options?.headerRow !== undefined) formData.append('header_row', String(options.headerRow));
    return api
      .post('/accounting/sales-invoices/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  /**
   * Print sales invoice
   * GET /accounting/sales-invoices/:id/print
   */
  getPrintData(id: string): Promise<ApiResponse<SalesInvoice>> {
    return api.get(`/accounting/sales-invoices/${id}/print`).then((r) => r.data);
  },

  /**
   * Preview unpost impact for a sales document
   * GET /accounting/sales-documents/:kind/:id/unpost-impact
   */
  previewUnpostImpact(kind: string, id: string): Promise<ApiResponse<UnpostImpactResponse>> {
    return api.get(`/accounting/sales-documents/${kind}/${id}/unpost-impact`).then((r) => r.data);
  },

  /**
   * Execute unpost (unpost) for a sales document
   * POST /accounting/sales-documents/:kind/:id/unpost
   */
  unpostDocument(
    kind: string,
    id: string,
    data: UnpostDocumentDto,
    idempotencyKey: string,
  ): Promise<ApiResponse<UnpostOperationResult>> {
    return api.post(`/accounting/sales-documents/${kind}/${id}/unpost`, data, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }).then((r) => r.data);
  },

  getSalesDocument(kind: string, id: string): Promise<ApiResponse<SalesDocumentDetail>> {
    return api.get(`/accounting/sales-documents/${kind}/${id}`).then((r) => r.data);
  },

  saveSalesDocumentDraft(
    kind: string,
    id: string,
    data: Record<string, unknown>,
  ): Promise<ApiResponse<Record<string, unknown>>> {
    return api.put(`/accounting/sales-documents/${kind}/${id}/draft`, data).then((r) => r.data);
  },

  /**
   * Issue e-invoice for a sales invoice
   * POST /accounting/sales-invoices/:id/issue
   */
  issueEInvoice(id: string): Promise<ApiResponse<any>> {
    return api.post(`/accounting/sales-invoices/${id}/issue`).then((r) => r.data);
  },

  createEInvoiceDraft(id: string, sourceType: 'sales_invoice' | 'sales_discount' | 'sales_return') {
    const path = sourceType === 'sales_return'
      ? `/accounting/sales-invoices/sales-returns/${id}/e-invoice-draft`
      : `/accounting/sales-invoices/${id}/e-invoice-draft`;
    return api.post(path).then((r) => r.data);
  },

  previewSalesDocumentEInvoice(
    id: string,
    sourceType: 'sales_invoice' | 'sales_discount' | 'sales_return',
    options: { invoice_date?: string; invoice_series?: string; invoice_template_series?: string; buyer_info?: Record<string, unknown> },
  ) {
    const path = sourceType === 'sales_return'
      ? `/accounting/sales-invoices/sales-returns/${id}/e-invoice-preview`
      : `/accounting/sales-invoices/${id}/e-invoice-preview`;
    return api.post(path, options).then((r) => r.data);
  },

  issueSalesDocumentEInvoice(
    id: string,
    sourceType: 'sales_invoice' | 'sales_discount' | 'sales_return',
    options: {
      invoice_date?: string;
      invoice_series?: string;
      invoice_template_series?: string;
      buyer_info?: Record<string, unknown>;
      send_email?: boolean;
      sync_if_source_changed?: boolean;
    },
  ) {
    const path = sourceType === 'sales_return'
      ? `/accounting/sales-invoices/sales-returns/${id}/issue`
      : `/accounting/sales-invoices/${id}/issue`;
    return api.post(path, options).then((r) => r.data);
  },

  postReplacement(id: string): Promise<ApiResponse<any>> {
    return api.post(`/accounting/sales-invoices/${id}/post-replacement`).then((r) => r.data);
  },

  postSalesDocumentDraft(
    kind: string,
    id: string,
    data: Record<string, unknown>,
  ): Promise<ApiResponse<Record<string, unknown>>> {
    return api.post(`/accounting/sales-documents/${kind}/${id}/post`, data).then((r) => r.data);
  },
};

export default salesInvoiceService;

// Types already exported at declaration — no need to re-export
